import React, { useState } from "react";
import {
    Box,
    Typography,
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Snackbar,
    Alert,
    Chip,
    Modal,
} from "@mui/material";
import {
    LocalPrintshop as LocalPrintshopIcon, LocationOn as LocationOnIcon, AccessTime as AccessTimeIcon, 
    DirectionsBusFilled as DirectionsBusFilledIcon, Person as PersonIcon
} from "@mui/icons-material";
import LineaDeTiempoBus from './LineaDeTiempoBus';
import { useManifiestos, usePasajeros, useRutasManifiesto } from "../../hooks/useManifest";
import { useParams } from "react-router-dom";
import { parseISO, isValid, format } from "date-fns";

const ManifiestoDetalleCliente = () => {
    const { viajeId } = useParams();
    const manifiestoIdNum = Number(viajeId);

    const { useFetchManifiesto, printManifestPDF } = useManifiestos(viajeId);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [openPdfModal, setOpenPdfModal] = useState(false);
    const { data: manifest, isLoading } = useFetchManifiesto(viajeId);
    const { paradas: paradasActuales } = useRutasManifiesto(viajeId);

    const origen = paradasActuales?.[0];
    const destino = paradasActuales?.at(-1);

    const isEnProceso = manifest?.manest === 'EN_PROCESO';

    const {
        pasajeros: passengers } = usePasajeros(manifiestoIdNum, {
            refetchInterval: isEnProceso ? 15000 : false, // ← Actualiza cada 10 segundos
        });

    const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });

    if (isLoading) return <CircularProgress />;

    const handleClosePdfModal = () => {
        if (pdfUrl) {
            URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
        }
        setOpenPdfModal(false);
    };

    const handleOpenPdf = async () => {
        const url = await printManifestPDF(viajeId);
        if (url) {
            setPdfUrl(url);
            setOpenPdfModal(true);
        }
    };

    const paradasConHora = (paradasActuales || [])
        .filter((p) => !!p.parhorest) // Solo paradas con hora definida
        .map((p) => {
            const hora = parseISO(p.parhorest);
            return {
                ...p,
                hora,
                horaStr: isValid(hora) ? format(hora, "HH:mm") : "N/D",
            };
        })
        .sort((a, b) => a.hora - b.hora);

    return (
        <Paper sx={{ p: 2 }}>
            <Box display="flex" flexDirection="column" gap={2} mb={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">
                        Detalle del Viaje: MN-<b>{String(viajeId).padStart(6, "0")}</b>
                    </Typography>
                    {(manifest.manest === 'EN_PROCESO' || manifest.manest === 'COMPLETADO') && (
                        <Box display="flex" gap={1}>
                            <Button
                                variant="contained"
                                startIcon={<LocalPrintshopIcon />}
                                onClick={handleOpenPdf}
                            >
                                Imprimir Manifiesto
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>

            <Box display="flex" alignItems="center" gap={1} mb={1}>
                <LocationOnIcon color="primary" />
                <Typography variant="body1">
                    <strong>Ruta:</strong>{" "}
                    <b>{origen?.locnom || "Origen N/D"}</b>
                    {origen?.parhorest && (
                        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", ml: 1 }}>
                            {manifest.manfec}
                            <AccessTimeIcon fontSize="small" sx={{ mr: 0.5 }} />
                            {format(new Date(origen.parhorest), "HH:mm")}
                        </Box>
                    )}
                    {" → "}
                    <b>{destino?.locnom || "Destino N/D"}</b>
                    {destino?.parhorest && (
                        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", ml: 1 }}>
                            {manifest.manfecfin}
                            <AccessTimeIcon fontSize="small" sx={{ mr: 0.5 }} />
                            {format(new Date(destino.parhorest), "HH:mm")}
                        </Box>
                    )}
                </Typography>
            </Box>
            <Box mt={1}>
                <LineaDeTiempoBus paradas={paradasConHora} />
            </Box>

            <Box mt={3} display="flex" gap={3}>
                {/* Vehículo */}
                <Paper
                    sx={{
                        flex: 1,
                        p: 3,
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: "#fffbe6",
                        borderRadius: 3,
                        boxShadow: 2,
                    }}
                >
                    <Box
                        sx={{
                            bgcolor: "#1976d2",
                            color: "#fff",
                            p: 1,
                            borderRadius: "50%",
                            mr: 3,
                        }}
                    >
                        <DirectionsBusFilledIcon sx={{ fontSize: 30 }} />
                    </Box>
                    <Box>
                        {manifest?.vehid ? (
                            <Box display="flex" flexWrap="wrap" gap={2} mt={1}>
                                <Typography variant="body3">
                                    <strong>Matrícula:</strong> {manifest.vehiculo?.VehMat}
                                </Typography>
                                <Typography variant="body3">
                                    <strong>Modelo:</strong> {manifest.vehiculo?.VehMod}
                                </Typography>
                                <Typography variant="body3">
                                    <strong>Capacidad:</strong> {manifest.vehiculo?.VehCap} pasajeros
                                </Typography>
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No hay vehículos asignados
                            </Typography>
                        )}
                    </Box>
                </Paper>

                {/* Conductor */}
                <Paper
                    sx={{
                        flex: 1,
                        p: 3,
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: "#fffbe6",
                        borderRadius: 3,
                        boxShadow: 2,
                    }}
                >
                    <Box
                        sx={{
                            bgcolor: "#1976d2",
                            color: "#fff",
                            p: 1,
                            borderRadius: "50%",
                            mr: 3,
                        }}
                    >
                        <PersonIcon sx={{ fontSize: 30 }} />
                    </Box>
                    <Box>
                        {manifest?.empleados ? (
                            <Box display="flex" flexWrap="wrap" gap={2} mt={1}>
                                <Typography variant="body3">
                                    <strong>Nombre:</strong> {manifest.empleados[0]?.PerNom}{" "}
                                    {manifest.empleados[0]?.PerApe}
                                </Typography>
                                <Typography variant="body3">
                                    <strong>DNI:</strong> {manifest.empleados[0]?.PerDNI}
                                </Typography>
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No hay conductor asignado
                            </Typography>
                        )}
                    </Box>
                </Paper>
            </Box>


            {/* Lista de pasajeros */}
            <Box mt={3}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle1" gutterBottom>
                        <strong>Lista de Pasajeros</strong>
                    </Typography>

                    <Typography variant="subtitle1" gutterBottom>
                        {`${passengers?.filter(p => p.mandetestpas === "CONFIRMADO").length || 0} / ${passengers?.length || 0} confirmados`}
                    </Typography>
                </Box>

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Nombre</strong></TableCell>
                                <TableCell><strong>Asiento</strong></TableCell>
                                <TableCell><strong>Punto de Subida</strong></TableCell>
                                <TableCell><strong>Punto de Bajada</strong></TableCell>
                                <TableCell><strong>Taxi</strong></TableCell>
                                <TableCell><strong>Estado</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(Array.isArray(passengers) ? passengers : []).map((passenger) => (
                                <TableRow key={passenger.mandetid}
                                    sx={{
                                        backgroundColor:
                                            passenger.mandetestpas === "CONFIRMADO"
                                                ? "#E8F5E9" // verde claro
                                                : passenger.mandetestpas === "NO VIAJÓ"
                                                    ? "#FFEBEE" // rojo claro
                                                    : "inherit", // por defecto
                                    }}
                                >
                                    <TableCell>{passenger.pernom} {passenger.perape}</TableCell>
                                    <TableCell>{passenger.mandetasi || "N/A"}</TableCell>
                                    <TableCell>{passenger.mandetpunsub || "N/A"}</TableCell>
                                    <TableCell>{passenger.mandetpunbaj || "N/A"}</TableCell>
                                    <TableCell>{passenger.mandettaxser ? "Sí" : "No"}</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>
                                        <Chip
                                            label={passenger.mandetestpas}
                                            size="small"
                                            color={
                                                passenger.mandetestpas === "CONFIRMADO"
                                                    ? "success"
                                                    : passenger.mandetestpas === "NO VIAJÓ"
                                                        ? "error"
                                                        : "info" // RESERVADO
                                            }
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>

                    </Table>
                </TableContainer>
            </Box>

            {/* Alertas */}
            <Snackbar open={alert.open} autoHideDuration={4000} onClose={() => setAlert({ open: false })}>
                <Alert severity={alert.severity}>{alert.message}</Alert>
            </Snackbar>

            <Modal open={openPdfModal} onClose={handleClosePdfModal} maxWidth="lg">
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '70%',
                        height: '80%',
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 2,
                        overflow: 'hidden',
                    }}
                >
                    {pdfUrl ? (
                        <iframe
                            src={pdfUrl}
                            title="Manifiesto PDF"
                            width="100%"
                            height="100%"
                            style={{ border: 'none' }}
                        />
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            Cargando PDF o URL inválida.
                        </Typography>
                    )}
                </Box>
            </Modal>
        </Paper>
    );
};

export default ManifiestoDetalleCliente;
